import { useTranslation } from 'react-i18next';
import type { UpdatesFilter, UpdatesTimeline } from './useUpdatesTimeline';
import { localDigits } from './updatesFormat';

interface UpdatesFilterChipsProps {
  timeline: UpdatesTimeline;
}

const CHIP =
  'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-solid px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-1';
const ON = 'border-sd-foreground bg-sd-foreground text-sd-background';
const OFF = 'border-sd-border bg-sd-card text-sd-foreground hover:bg-sd-muted';

/** All · Comments n · Calls n · WhatsApp n — toggle buttons, one pressed at a time. */
export function UpdatesFilterChips({ timeline }: UpdatesFilterChipsProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { filter, setFilter, counts } = timeline;
  const chips: { key: UpdatesFilter; label: string; count?: number }[] = [
    { key: 'all', label: String(t('all')) },
    // BOXY-587 — comments first, then calls, then WhatsApp.
    { key: 'comments', label: String(t('updates_filter_comments')), count: counts.comments },
    { key: 'calls', label: String(t('updates_filter_calls')), count: counts.calls },
    { key: 'whatsapp', label: String(t('updates_filter_whatsapp')), count: counts.whatsapp },
  ];

  return (
    <div role='group' aria-label={String(t('updates_filters'))} className='flex flex-wrap gap-2'>
      {chips.map(({ key, label, count }) => {
        const on = filter === key;
        return (
          <button
            key={key}
            type='button'
            aria-pressed={on}
            onClick={() => setFilter(key)}
            className={`${CHIP} ${on ? ON : OFF}`}
          >
            {label}
            {count ? (
              <span
                className={`min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] font-semibold tabular-nums ${on ? 'bg-sd-background/20' : 'bg-sd-muted text-sd-muted-foreground'}`}
              >
                {localDigits(String(count), lang)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
